import { useQuery } from "@tanstack/react-query";
import { getOrganizationId, getSelectedContext } from "@/lib/api";
import { fetchSubscription } from "@/hooks/useBilling";
import { retryUnlessClientError } from "@/lib/queryRetry";
import type { SubscriptionResponse } from "@/types/billing";

/**
 * Shared subscription-gate query. Used by both ProtectedRoute (for the
 * outer-route admin redirect) and RequireSubscription (for the per-page
 * lock card on settings sub-routes). Single React Query keyed off
 * (orgId, outletId) so both consumers share one network call.
 *
 * Cache-and-retry config tuned for subscription-gate correctness:
 *   - staleTime 2 min: most navigations reuse the cached answer, so a
 *     single bad fetch can't make the banner flash on the next route.
 *   - gcTime 30 min: survives brief idle; paired with refetchOnWindowFocus
 *     keeps state fresh after the user comes back.
 *   - retry up to 3 times with exponential backoff. "No plan" (200 null or
 *     404) isn't an error; other 4xx such as a 403 are definitive and NOT
 *     retried; network errors, 5xx and 401 are treated as transient.
 *
 * Callers fail OPEN on ambiguity (loading or errored without cached
 * answer): the backend rejects expired accounts on every real API call,
 * so showing the UI briefly while we can't tell is a better UX trade-off
 * than flashing a false "Subscription Expired" on a network hiccup.
 */
export function useSubscriptionGateQuery() {
  const orgId = getOrganizationId();
  const context = getSelectedContext();
  const outletId = context?.outlet?.id ?? null;
  return useQuery({
    queryKey: ["billing-subscription", orgId, outletId] as const,
    queryFn: async (): Promise<SubscriptionResponse | null> => {
      if (!orgId || !outletId) return null;
      return fetchSubscription(orgId, outletId);
    },
    enabled: !!orgId && !!outletId,
    staleTime: 2 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: retryUnlessClientError,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}
